import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import * as StructuredTableTogglableStateActions from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableTogglableStateActions';
import {
  AxisContextMenuActionFunctionParameters,
  RowContextMenuActionFunction,
  StructuredTableContextMenuItem,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getErrorsAndWarningsFromRow} from 'notebook/tables/errors/CellErrorGetters';
import {hasAnyWarnings} from 'notebook/tables/excess/Util';
import {SelectionRange, TableDimension} from 'notebook/tables/TableConstants';
import getDimensionalOperationText from 'notebook/tables/util/getDimensionalOperationText';
import {isRowArchived} from 'notebook/tables/util/isRowArchived';
import APIUtil from 'util/APIUtil';

import rowHasUnsubmittedChanges from '../util/rowHasUnsubmittedChanges';

/*
 * Type for the indices of a row selection. Represents the interval [start,end)
 * start: beginning of range, inclusive
 * end: end of range, exclusive
 */
export interface RowSubmissionRange {
  start: number;
  end: number;
}

/**
 * Function to get the appropriate loading message depending on if the user is submitting a single row or
 * multiple rows in a given structured table kind.
 *
 * @param selection the range of rows the user is submitting
 * @param multiMessage message for when the user is submitting more than one row
 * @param singleMessage message for when the user is submitting a single row
 * @returns
 */
export function getRowAwareLoadingMessage(
  selection: RowSubmissionRange,
  multiMessage: string,
  singleMessage: string
): string {
  return selection.end - selection.start > 1
    ? `${multiMessage} ${selection.start + 1} - ${selection.end}...`
    : `${singleMessage} ${selection.start + 1}...`;
}

/**
 * Helper for making an action function to submit a row in your structured table.
 */
interface MakeSubmitRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata> {
  allowOutsideOfEntry?: boolean;
  onSelectForSubmit: (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => void;
}

function shouldShowMenuItem<TTableMetadata, TRowMetadata>(
  configurationParams: MakeSubmitRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata>,
  actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
): boolean {
  return (
    // Some rows are selected
    actionFunctionParams.selectionRange != null &&
    // The table is not read-only
    !actionFunctionParams.locked &&
    // The table is in a notebook entry (or non-entry action function is explicitly allowed)
    (configurationParams.allowOutsideOfEntry || actionFunctionParams.entryOrTemplateId != null) &&
    // The table is not in a template
    (configurationParams.allowOutsideOfEntry ||
      APIUtil.isBaseEntry(actionFunctionParams.entryOrTemplateId!)) && // The table is not a togglable-state table that is currently being edited
    StructuredTableTogglableStateActions.getStructuredTableTogglableStatus(
      actionFunctionParams.structuredTable.cid
    ) !== StructuredTableEditStatus.EDITING &&
    // The table is not a config type
    !actionFunctionParams.structuredTable.isConfig
  );
}

function getSelectedRowIndices(selectionRange: SelectionRange): Array<number> {
  const numSelectedRows = selectionRange.maxIndex - selectionRange.minIndex + 1;
  return new Array(numSelectedRows).fill(null).map((_, i) => selectionRange.minIndex + i);
}

function getRowAxisContextMenuItem<TTableMetadata, TRowMetadata>(
  configurationParams: MakeSubmitRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata>,
  actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
): StructuredTableContextMenuItem | null {
  if (!shouldShowMenuItem(configurationParams, actionFunctionParams)) {
    return null;
  }

  const name = getDimensionalOperationText({
    verb: 'Submit',
    selectionRange: actionFunctionParams.selectionRange,
    dimension: TableDimension.ROW,
  });

  const selectedRowIndices = getSelectedRowIndices(actionFunctionParams.selectionRange!);

  const areAllSelectedRowsUnsubmitted = selectedRowIndices.every((idx) => {
    if (idx >= actionFunctionParams.computedTable.length) {
      // Sanity check that the row is within the bounds of the table
      return false;
    }

    const columns = actionFunctionParams.structuredTableAdapter.getColumnsForRow(
      actionFunctionParams.structuredTable,
      actionFunctionParams.structuredTable.rows[idx]
    );
    return (
      // The row has unsubmitted changes
      rowHasUnsubmittedChanges(
        actionFunctionParams.computedTable[idx],
        actionFunctionParams.structuredTable.rows[idx],
        columns,
        actionFunctionParams.structuredTable.schemas
      ) &&
      // and the row has no errors in the structuredTable
      getErrorsAndWarningsFromRow(
        actionFunctionParams.structuredTable.rows[idx].cells,
        actionFunctionParams.computedTable[idx]
      ).every((error) => hasAnyWarnings(error)) &&
      // and the row has no errors in the table
      (!actionFunctionParams.table ||
        getErrorsAndWarningsFromRow(
          actionFunctionParams.table.table[idx],
          actionFunctionParams.computedTable[idx]
        ).every((error) => hasAnyWarnings(error))) &&
      // and the row is not archived
      actionFunctionParams.table?.rowData?.[idx].uiData?.archivePurpose == null &&
      !isRowArchived(actionFunctionParams.structuredTable.rows[idx])
    );
  });

  if (!areAllSelectedRowsUnsubmitted) {
    // As a UX choice, we disable the option if the selection does not consist entirely of unsubmitted rows.
    return null;
  }

  return {
    name,
    onSelect: () => {
      trackRowBasedTableSubmission({
        fileId: actionFunctionParams.entryOrTemplateId,
        tableKind: actionFunctionParams.structuredTable.kind,
        rowCount: selectedRowIndices.length,
        tableRowCount: actionFunctionParams.structuredTable.rows.length,
      });
      configurationParams.onSelectForSubmit(actionFunctionParams);
    },
  };
}

function trackRowBasedTableSubmission(metadata: {
  fileId: string | null;
  tableKind: StructuredTableKind;
  rowCount: number;
  tableRowCount: number;
}): void {
  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.ROW_BASED_TABLE_SUBMISSION, metadata);
}

export default function MakeSubmitRowActionFunction<TTableMetadata, TRowMetadata>(
  configurationParams: MakeSubmitRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata>
): RowContextMenuActionFunction<TTableMetadata, TRowMetadata> {
  return {
    getRowAxisContextMenuItem: (
      actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
    ) => getRowAxisContextMenuItem(configurationParams, actionFunctionParams),
  };
}
